import React from 'react';
import '../../TemplateReaderWaasFixes.css';

const fallbackCanvas = {
  width: 1120,
  height: 680,
  background: 'linear-gradient(135deg,#07111f,#1d4ed8)',
  elements: [
    {
      id: 'canvas-text-default',
      type: 'text',
      x: 76,
      y: 86,
      width: 620,
      height: 250,
      zIndex: 3,
      content: {
        eyebrow: 'CANVAS SECTION',
        heading: 'Build freely with text, image, grid and button layers.',
        body: 'This section is saved as structured canvas data so builder preview and reader render the same positions.'
      },
      style: {
        textAlign: 'left',
        headingSize: 58,
        headingColor: '#ffffff',
        bodyColor: 'rgba(255,255,255,.78)',
        eyebrowColor: '#93c5fd',
        fontWeight: 800
      }
    },
    {
      id: 'canvas-image-default',
      type: 'image',
      x: 720,
      y: 92,
      width: 340,
      height: 290,
      zIndex: 2,
      content: {
        src: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80&fit=crop&auto=format',
        alt: 'Canvas visual'
      },
      style: {
        radius: 28,
        objectFit: 'cover',
        shadow: '0 30px 70px rgba(0,0,0,.28)'
      }
    },
    {
      id: 'canvas-button-default',
      type: 'button',
      x: 76,
      y: 390,
      width: 210,
      height: 52,
      zIndex: 4,
      content: { label: 'Start project', url: '/contactus' },
      style: { background: '#ffffff', color: '#07111f', radius: 999 }
    }
  ]
};

const px = (value, fallback = 0) => {
  const next = value === undefined || value === null || value === '' ? fallback : value;
  return typeof next === 'number' ? `${next}px` : String(next);
};

const canvasData = (section = {}) => ({
  ...fallbackCanvas,
  ...(section.content?.canvas || {}),
  elements: Array.isArray(section.content?.canvas?.elements) && section.content.canvas.elements.length
    ? section.content.canvas.elements
    : fallbackCanvas.elements
});

const elementFrameStyle = (element = {}) => ({
  position: 'absolute',
  left: px(element.x),
  top: px(element.y),
  width: px(element.width, 200),
  height: px(element.height, 80),
  zIndex: element.zIndex || 1,
  textAlign: element.style?.textAlign || element.style?.align || 'left',
  color: element.style?.color,
  background: element.style?.background,
  border: element.style?.border,
  borderRadius: px(element.style?.radius ?? element.style?.borderRadius, 0),
  boxShadow: element.style?.shadow,
  opacity: element.style?.opacity,
  overflow: element.style?.overflow || (element.type === 'image' ? 'hidden' : 'visible'),
  padding: element.style?.padding,
  boxSizing: 'border-box',
  fontFamily: element.style?.fontFamily
});

const renderText = (element) => {
  const content = element.content || {};
  const style = element.style || {};
  return (
    <div data-canvas-element-id={element.id} style={elementFrameStyle(element)}>
      {content.eyebrow && <p style={{ margin: '0 0 12px', color: style.eyebrowColor || 'var(--template-accent-color,#93c5fd)', fontWeight: 900, letterSpacing: '.13em', textTransform: 'uppercase' }}>{content.eyebrow}</p>}
      {content.heading && <h2 style={{ margin: 0, color: style.headingColor || style.color || 'var(--template-text-color,#fff)', fontFamily: style.fontFamily, fontSize: px(style.headingSize, 48), lineHeight: style.headingLineHeight || 1.08, fontWeight: style.fontWeight || 'var(--template-heading-weight,800)' }}>{content.heading}</h2>}
      {content.body && <p style={{ margin: '14px 0 0', color: style.bodyColor || 'var(--template-muted-color,rgba(255,255,255,.78))', fontFamily: style.fontFamily, fontSize: px(style.bodySize, 16), lineHeight: style.bodyLineHeight || 1.6 }}>{content.body}</p>}
    </div>
  );
};

const renderImage = (element) => {
  const content = element.content || {};
  return (
    <div data-canvas-element-id={element.id} style={elementFrameStyle(element)}>
      {content.src && <img src={content.src} alt={content.alt || ''} style={{ width: '100%', height: '100%', objectFit: element.style?.objectFit || 'cover', objectPosition: element.style?.objectPosition || 'center', display: 'block' }} />}
    </div>
  );
};

const renderButton = (element) => {
  const content = element.content || {};
  const style = element.style || {};
  return (
    <a data-canvas-element-id={element.id} href={content.url || '#'} style={{ ...elementFrameStyle(element), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontFamily: style.fontFamily, fontSize: px(style.fontSize, 14), fontWeight: style.fontWeight || 800, letterSpacing: style.letterSpacing || '.04em', background: style.background || 'var(--template-primary-color,#23196E)', color: style.color || '#ffffff', borderRadius: px(style.radius ?? style.borderRadius, '999px') }}>
      {content.label || 'Button'}
    </a>
  );
};

const renderGrid = (element) => {
  const content = element.content || {};
  const images = Array.isArray(content.images) ? content.images : [];
  return (
    <div data-canvas-element-id={element.id} style={{ ...elementFrameStyle(element), display: 'grid', gridTemplateColumns: `repeat(${element.layout?.columns || element.style?.columns || 3}, minmax(0,1fr))`, gap: px(element.layout?.gap || element.style?.gap, 14) }}>
      {images.map((image, index) => <img key={`${image.src}-${index}`} src={image.src} alt={image.alt || ''} style={{ width: '100%', height: '100%', minHeight: 0, objectFit: element.style?.objectFit || 'cover', borderRadius: px(element.style?.imageRadius, 16) }} />)}
    </div>
  );
};

const renderElement = (element) => {
  if (element.type === 'image') return renderImage(element);
  if (element.type === 'button') return renderButton(element);
  if (element.type === 'imageGrid' || element.type === 'grid') return renderGrid(element);
  return renderText(element);
};

const CanvasSectionRenderer = ({ section, theme = {}, mode = 'reader', onSelectElement }) => {
  const canvas = canvasData(section);
  return (
    <section style={{ background: section.style?.backgroundColor || theme.background || 'var(--template-background-color,#fff)', color: theme.color || 'var(--template-text-color,#111827)', padding: section.style?.padding || '72px 24px', overflowX: 'auto' }}>
      <div style={{ width: px(canvas.width, 1120), minWidth: px(canvas.width, 1120), margin: '0 auto' }}>
        <div
          data-template-canvas-section="true"
          data-canvas-mode={mode}
          style={{ position: 'relative', width: px(canvas.width, 1120), height: px(canvas.height, 680), overflow: 'hidden', background: canvas.background || theme.surface || '#0b1020', borderRadius: px(canvas.radius, 28), boxShadow: canvas.shadow || theme.cardShadow || '0 30px 80px rgba(15,23,42,.18)' }}
        >
          {(canvas.elements || []).map((element) => (
            <div key={element.id} data-canvas-wrapper="true" onClick={mode === 'builder' ? (event) => { event.stopPropagation(); onSelectElement?.(element.id); } : undefined} style={{ display: 'contents' }}>
              {renderElement(element)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CanvasSectionRenderer;
